/* Capital Path - theme.js
   Vanilla JS only. Everything degrades gracefully without it. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Sticky header ---------------- */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;

    var setState = function () {
      if (window.scrollY > 12) {
        header.classList.add('is-solid');
      } else {
        header.classList.remove('is-solid');
      }
    };
    setState();
    window.addEventListener('scroll', setState, { passive: true });

    var setHeight = function () {
      document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    };
    setHeight();
    window.addEventListener('resize', setHeight);
  }

  /* ---------------- Mobile drawer ---------------- */
  function initDrawer() {
    var toggle = document.querySelector('[data-nav-toggle]');
    var drawer = document.querySelector('[data-drawer]');
    if (!toggle || !drawer) return;

    var closers = drawer.querySelectorAll('[data-drawer-close]');
    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      drawer.removeAttribute('aria-hidden');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      var first = drawer.querySelector('a, button');
      if (first) first.focus();
    }

    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (lastFocus) lastFocus.focus();
    }

    toggle.addEventListener('click', function () {
      if (drawer.classList.contains('is-open')) { close(); } else { open(); }
    });

    Array.prototype.forEach.call(closers, function (el) {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
      if (e.key !== 'Tab' || !drawer.classList.contains('is-open')) return;
      var focusables = drawer.querySelectorAll('a[href], button:not([disabled]), input, select, textarea');
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    });
  }

  /* ---------------- Announcement dismiss ---------------- */
  function initAnnouncement() {
    var bar = document.querySelector('[data-announcement]');
    if (!bar) return;
    var close = bar.querySelector('[data-announcement-close]');
    if (!close) return;
    close.addEventListener('click', function () {
      bar.style.display = 'none';
      try { sessionStorage.setItem('cp-announcement-dismissed', '1'); } catch (err) { /* ignore */ }
    });
    try {
      if (sessionStorage.getItem('cp-announcement-dismissed') === '1') bar.style.display = 'none';
    } catch (err) { /* ignore */ }
  }

  /* ---------------- Scroll reveals ---------------- */
  function initReveals() {
    var items = document.querySelectorAll('.reveal');
    if (!items.length) return;
    if (reduceMotion || !('IntersectionObserver' in window) || !document.body.classList.contains('animations-on')) {
      Array.prototype.forEach.call(items, function (el) { el.classList.add('is-visible'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    Array.prototype.forEach.call(items, function (el) { observer.observe(el); });
  }

  /* ---------------- Accordions ---------------- */
  function initAccordions() {
    var triggers = document.querySelectorAll('[data-accordion-trigger]');
    Array.prototype.forEach.call(triggers, function (trigger) {
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;

      if (trigger.getAttribute('aria-expanded') === 'true') {
        panel.style.maxHeight = panel.scrollHeight + 'px';
      }

      trigger.addEventListener('click', function () {
        var expanded = trigger.getAttribute('aria-expanded') === 'true';
        var group = trigger.closest('[data-accordion]');
        if (group && group.getAttribute('data-accordion-single') === 'true' && !expanded) {
          var others = group.querySelectorAll('[data-accordion-trigger]');
          Array.prototype.forEach.call(others, function (other) {
            if (other === trigger) return;
            other.setAttribute('aria-expanded', 'false');
            var otherPanel = document.getElementById(other.getAttribute('aria-controls'));
            if (otherPanel) otherPanel.style.maxHeight = null;
          });
        }
        trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        panel.style.maxHeight = expanded ? null : panel.scrollHeight + 'px';
      });
    });

    window.addEventListener('resize', function () {
      var open = document.querySelectorAll('[data-accordion-trigger][aria-expanded="true"]');
      Array.prototype.forEach.call(open, function (trigger) {
        var panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (panel) panel.style.maxHeight = panel.scrollHeight + 'px';
      });
    });
  }

  /* ---------------- Back to top ---------------- */
  function initBackToTop() {
    var button = document.querySelector('[data-back-to-top]');
    if (!button) return;
    button.hidden = false;
    var check = function () {
      if (window.scrollY > 600) {
        button.classList.add('is-visible');
      } else {
        button.classList.remove('is-visible');
      }
    };
    check();
    window.addEventListener('scroll', check, { passive: true });
    button.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  }

  /* ---------------- Reading progress ---------------- */
  function initProgress() {
    var bar = document.querySelector('[data-progress-bar]');
    var content = document.querySelector('[data-article-content]');
    if (!bar || !content) return;

    var update = function () {
      var rect = content.getBoundingClientRect();
      var total = rect.height - window.innerHeight;
      var scrolled = -rect.top;
      var pct = total > 0 ? (scrolled / total) * 100 : 0;
      pct = Math.min(100, Math.max(0, pct));
      bar.style.width = pct + '%';
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  }

  /* ---------------- Table of contents ---------------- */
  function slugify(text, used) {
    var base = text.toLowerCase().trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
    if (!base) base = 'section';
    var id = base;
    var i = 2;
    while (used[id]) {
      id = base + '-' + i;
      i++;
    }
    used[id] = true;
    return id;
  }

  function initToc() {
    var targets = document.querySelectorAll('[data-toc]');
    var content = document.querySelector('[data-article-content]');
    if (!targets.length || !content) return;

    var headings = content.querySelectorAll('h2, h3');
    if (!headings.length) {
      Array.prototype.forEach.call(targets, function (el) {
        var wrapper = el.closest('[data-toc-wrapper]');
        if (wrapper) wrapper.remove();
      });
      return;
    }

    var used = {};
    Array.prototype.forEach.call(document.querySelectorAll('[id]'), function (el) { used[el.id] = true; });

    var links = [];
    Array.prototype.forEach.call(headings, function (heading) {
      if (!heading.id) heading.id = slugify(heading.textContent, used);
      links.push({ id: heading.id, text: heading.textContent, level: heading.tagName.toLowerCase() });
    });

    Array.prototype.forEach.call(targets, function (list) {
      links.forEach(function (link) {
        var li = document.createElement('li');
        if (link.level === 'h3') li.className = 'toc__sub';
        var a = document.createElement('a');
        a.href = '#' + link.id;
        a.textContent = link.text;
        a.addEventListener('click', function (e) {
          e.preventDefault();
          var target = document.getElementById(link.id);
          if (!target) return;
          target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
          history.replaceState(null, '', '#' + link.id);
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        });
        li.appendChild(a);
        list.appendChild(li);
      });
    });

    if (!('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var id = entry.target.id;
        var all = document.querySelectorAll('[data-toc] a');
        Array.prototype.forEach.call(all, function (a) {
          a.classList.toggle('is-active', a.getAttribute('href') === '#' + id);
        });
      });
    }, { rootMargin: '-90px 0px -70% 0px' });
    Array.prototype.forEach.call(headings, function (h) { observer.observe(h); });
  }

  /* ---------------- Copy link share ---------------- */
  function initShare() {
    var button = document.querySelector('[data-copy-link]');
    if (!button) return;
    button.addEventListener('click', function () {
      var url = button.getAttribute('data-url') || window.location.href;
      var done = function () {
        var original = button.getAttribute('aria-label');
        button.setAttribute('aria-label', 'Link copied');
        window.setTimeout(function () { button.setAttribute('aria-label', original); }, 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done);
      } else {
        var input = document.createElement('input');
        input.value = url;
        document.body.appendChild(input);
        input.select();
        try { document.execCommand('copy'); done(); } catch (err) { /* ignore */ }
        document.body.removeChild(input);
      }
    });
  }

  function init() {
    window.__capitalPathReady = true;
    initHeader();
    initDrawer();
    initAnnouncement();
    initReveals();
    initAccordions();
    initBackToTop();
    initProgress();
    initToc();
    initShare();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
